import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Row, Col, Descriptions, Button, Space, Tabs, message, Spin, Modal, Tag } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, FileTextOutlined, FileOutlined } from '@ant-design/icons';
import axios from '../services/axiosConfig';
import { CONTRACT_API, FACTORY_API, TENANT_API, INVOICE_API } from '../constants/api';
import moment from 'moment';
import ContractForm from '../components/contracts/ContractForm';
import DepositManagement from '../components/deposit/DepositManagement';
import PaymentHistory from '../components/payment/PaymentHistory';

const ContractDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Xử lý ID hợp đồng có thể có nhiều dấu /
  const getContractId = () => {
    // Sử dụng location.pathname để lấy đường dẫn đầy đủ
    const pathname = location.pathname;
    
    // Tách chuỗi để lấy phần ID sau "contracts/" hoặc "contract/"
    let path = '';
    if (pathname.includes('/contracts/')) {
      path = pathname.split('/contracts/')[1];
    } else if (pathname.includes('/contract/')) {
      path = pathname.split('/contract/')[1];
    }
    
    console.log('Đường dẫn đầy đủ:', pathname);
    console.log('ID hợp đồng từ đường dẫn:', path);
    
    return path || params.id || '';
  };
  
  const id = getContractId();
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [factories, setFactories] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletingFiles, setDeletingFiles] = useState({});

  // Format tiền tệ
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(amount);
  };

  // Format ngày
  const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY');
  };

  // Lấy chi tiết hợp đồng
  const fetchContractDetail = async () => {
    try {
      setLoading(true);
      console.log('ID hợp đồng:', id);
      
      // Sử dụng encodeURIComponent cho ID hợp đồng để đảm bảo các ký tự đặc biệt được xử lý đúng
      const decodedId = decodeURIComponent(id);
      console.log('ID hợp đồng đã giải mã:', decodedId);
      
      const response = await axios.get(CONTRACT_API.DETAIL(decodedId));
      console.log('Dữ liệu hợp đồng từ API:', response.data);
      // Lấy thêm lịch sử thanh toán cho hợp đồng
      try {
        const paymentResponse = await axios.get(INVOICE_API.CONTRACT_PAYMENT_HISTORY(decodedId));
        // Log dữ liệu để kiểm tra cấu trúc
        console.log('Dữ liệu thanh toán từ API:', paymentResponse.data);
        
        // Gán dữ liệu thanh toán vào đối tượng hợp đồng
        response.data.payments = paymentResponse.data.data.payments || [];
        response.data.totalPaid = paymentResponse.data.data.totalPaid || 0;
      } catch (paymentError) {
        console.error('Lỗi khi tải lịch sử thanh toán:', paymentError);
        response.data.payments = [];
        response.data.totalPaid = 0;
      }
      
      setContract(response.data);
    } catch (error) {
      console.error('Lỗi khi tải chi tiết hợp đồng:', error);
      message.error('Không thể tải thông tin hợp đồng');
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách nhà xưởng và khách hàng
  const fetchFactoriesAndTenants = async () => {
    try {
      const factoriesResponse = await axios.get(FACTORY_API.LIST);
      const tenantsResponse = await axios.get(TENANT_API.LIST);
      
      setFactories(factoriesResponse.data.factories || []);
      setTenants(tenantsResponse.data.tenants || []);
    } catch (error) {
      console.error('Lỗi khi tải danh sách nhà xưởng và khách hàng:', error);
    }
  };

  useEffect(() => {
    fetchContractDetail();
    fetchFactoriesAndTenants();
  }, [id]);

  // Xử lý cập nhật hợp đồng
  const handleUpdateContract = async (updatedContract) => {
    try {
      const decodedId = decodeURIComponent(id);
      await axios.put(CONTRACT_API.UPDATE(decodedId), updatedContract);
      message.success('Cập nhật hợp đồng thành công');
      fetchContractDetail();
      setEditModalVisible(false);
    } catch (error) {
      console.error('Lỗi khi cập nhật hợp đồng:', error);
      message.error('Không thể cập nhật hợp đồng');
    }
  };

  // Xử lý xóa hợp đồng
  const handleDeleteContract = () => {
    Modal.confirm({
      title: 'Xác nhận xóa hợp đồng',
      content: 'Bạn có chắc chắn muốn xóa hợp đồng này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const decodedId = decodeURIComponent(id);
          await axios.delete(CONTRACT_API.DELETE(decodedId));
          message.success('Xóa hợp đồng thành công');
          navigate('/contracts');
        } catch (error) {
          console.error('Lỗi khi xóa hợp đồng:', error);
          message.error('Không thể xóa hợp đồng');
        }
      }
    });
  };

  // Xem file hợp đồng
  const handleViewFile = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  // Xử lý chuỗi ContractFile để lấy danh sách file
  const parseContractFiles = (contractFileStr) => {
    if (!contractFileStr) return [];
    
    try {
      return JSON.parse(contractFileStr);
    } catch (error) {
      console.error('Lỗi khi phân tích chuỗi file hợp đồng:', error);
      return [contractFileStr]; // Nếu không phải chuỗi JSON, trả về file đơn
    }
  };

  const getFileName = (filePath) => {
    if (!filePath) return '';
    const parts = filePath.split('\\');
    return parts[parts.length - 1];
  };

  const handleDeleteFile = async (file) => {
    try {
      Modal.confirm({
        title: 'Xác nhận xóa',
        content: `Bạn có chắc chắn muốn xóa file "${getFileName(file)}"?`,
        okText: 'Xóa',
        okType: 'danger',
        cancelText: 'Hủy',
        onOk: async () => {
          setDeletingFiles(prev => ({ ...prev, [file]: true }));
          try {
            const response = await axios.delete(CONTRACT_API.DELETE_FILE(contract.ContractID), {
              data: { filePath: file }
            });
            
            if (response.data.success) {
              message.success('Xóa file hợp đồng thành công');
              // Cập nhật lại danh sách file
              const updatedContract = { ...contract };
              const files = parseContractFiles(updatedContract.ContractFile);
              const updatedFiles = files.filter(f => f !== file);
              updatedContract.ContractFile = updatedFiles.join(',');
              setContract(updatedContract);
            }
          } catch (error) {
            console.error('Lỗi khi xóa file:', error);
            message.error('Không thể xóa file hợp đồng');
          } finally {
            setDeletingFiles(prev => ({ ...prev, [file]: false }));
          }
        }
      });
    } catch (error) {
      console.error('Lỗi khi xóa file:', error);
      message.error('Không thể xóa file hợp đồng');
    }
  };

  // Tab items
  const items = [
    {
      key: '1',
      label: 'Thông tin chung',
      children: (
        <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
          <Descriptions.Item label="Mã hợp đồng" span={2}>
            {contract?.ContractID}
          </Descriptions.Item>
          
          <Descriptions.Item label="Nhà xưởng">
            {contract?.Factory?.FactoryName}
          </Descriptions.Item>
          
          <Descriptions.Item label="Khách thuê">
            {contract?.Tenant?.TenantName}
          </Descriptions.Item>
          
          <Descriptions.Item label="Ngày ký">
            {contract?.SignDate ? formatDate(contract.SignDate) : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Ngày bắt đầu">
            {contract?.StartDate ? formatDate(contract.StartDate) : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Ngày kết thúc">
            {contract?.EndDate ? formatDate(contract.EndDate) : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Thời hạn (tháng)">
            {contract?.Duration || '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Phí thuê hàng tháng">
            {contract?.RentAmount ? formatCurrency(contract.RentAmount) : '--'}
          </Descriptions.Item>

          <Descriptions.Item label="Phí dịch vụ/tháng">
            {contract?.ServiceFee ? formatCurrency(contract.ServiceFee) : '--'}
          </Descriptions.Item>

          <Descriptions.Item label="Phí hạ tầng/tháng">
            {contract?.InfrastructureFee ? formatCurrency(contract.InfrastructureFee) : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Kỳ thanh toán">
            {contract?.PaymentTerm ? `${contract.PaymentTerm} tháng` : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Tăng giá hàng năm">
            {contract?.YearlyIncreaseRate ? `${contract.YearlyIncreaseRate}%` : '0%'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Ngày áp dụng tăng giá">
            {contract?.IncreaseRateDate ? formatDate(contract.IncreaseRateDate) : '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Trạng thái" span={2}>
            <Tag color={contract?.Status === 'Active' ? 'green' : contract?.Status === 'Expiring' ? 'orange' : 'red'}>
              {contract?.Status === 'Active' ? 'Đang hiệu lực' : contract?.Status === 'Expiring' ? 'Sắp hết hạn' : 'Đã hết hạn'}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label="Ghi chú" span={2}>
            {contract?.Note || '--'}
          </Descriptions.Item>
          
          <Descriptions.Item label="File hợp đồng" span={2}>
            {contract?.ContractFile ? (
              <Space direction="vertical">
                {parseContractFiles(contract.ContractFile).map((file, index) => (
                  <Space key={index}>
                    <Button
                      key={index}
                      type="link" 
                      icon={<FileOutlined />}
                      onClick={() => handleViewFile(`${process.env.REACT_APP_API_URL}/${file}`)}
                    >
                      {getFileName(file)}
                    </Button>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFile(file)}
                      loading={deletingFiles[file]}
                    />
                  </Space>
                ))}
              </Space>
            ) : 'Chưa có file'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: '2',
      label: 'Tiền đặt cọc',
      children: (
        <DepositManagement 
          contractId={decodeURIComponent(id)}
          contractData={contract}
        />
      ),
    },
    {
      key: '3',
      label: 'Lịch sử thanh toán',
      children: <PaymentHistory 
        contractId={decodeURIComponent(id)} 
        contract={contract}
        paymentData={contract?.payments || []} 
      />,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Card 
        title={
          <Space>
            <Button 
              type="text" 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/contracts')}
            />
            <span>Chi tiết hợp đồng: {contract?.ContractID}</span>
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              onClick={() => setEditModalVisible(true)}
            >
              Chỉnh sửa
            </Button>
            <Button 
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteContract}
            >
              Xóa
            </Button>
          </Space>
        }
      >
        <Tabs items={items} defaultActiveKey="1" />
      </Card>

      {/* Modal chỉnh sửa hợp đồng */}
      <ContractForm
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onSubmit={handleUpdateContract}
        initialValues={contract}
        title="Cập nhật hợp đồng"
        factories={factories}
        tenants={tenants}
      />
    </div>
  );
};

export default ContractDetail; 